/**
 * The LLM cites assets inline with the platform-wide asset-code convention `SB{V,I,A}-<contentId>`
 * (see ASSET_ID_PATTERN in search-gateway-client), optionally parenthesized, so the client can
 * build a top-results tray from them. The exact token is still being tuned in the system prompt,
 * so matching is deliberately lenient (case-insensitive, optional wrapping parens) and this module
 * is the single source of truth for the format — a format change should only ever touch these
 * regexes. The digits of an asset code are the item's contentId.
 */
const ASSET_CODE = /\bSB[VIA]-(\d+)\b/gi;

// A parenthesized code is removed with both parens so an outer parenthetical is never
// unbalanced; a bare code needs word boundaries so prose like SBV-123abc is left alone.
const WRAPPED_OR_BARE_CODE = String.raw`(?:\(\s*SB[VIA]-\d+\s*\)|\bSB[VIA]-\d+\b)`;

// Only spaces/tabs around a code are consumed, never newlines, so stripping cannot merge
// lines or paragraphs of the markdown source. A code at the start of a line keeps the line
// break and gives up its trailing spaces instead of its (nonexistent) leading ones.
const LINE_START_MARKER = new RegExp(String.raw`(^|\n)[ \t]*${WRAPPED_OR_BARE_CODE}[ \t]*`, 'gi');
const MID_LINE_MARKER = new RegExp(String.raw`[ \t]*${WRAPPED_OR_BARE_CODE}`, 'gi');

// An asset code still streaming in at the end of the text (`SB`, `SBV-`, `(SBV-35`) is hidden
// so the raw code never flashes in the conversation pane.
const UNTERMINATED_TRAILING_CODE = /[ \t]*\(?\s*\bSB([VIA](-\d*)?)?$/i;

/** Returns the cited contentIds in citation order, deduplicated. */
export function extractCitedContentIds(text: string): number[] {
    const ids = [...text.matchAll(ASSET_CODE)].map((match) => Number(match[1]));
    return [...new Set(ids)];
}

/** Returns the text with all asset codes (including a half-streamed trailing one) removed. */
export function stripAssetMarkers(text: string): string {
    // The trailing pass must run first: a half-streamed `(SBV-35` would otherwise have its
    // bare code stripped by the mid-line pass, stranding the opening paren.
    return text.replace(UNTERMINATED_TRAILING_CODE, '').replace(LINE_START_MARKER, '$1').replace(MID_LINE_MARKER, '');
}
